import React, { useState, useEffect } from 'react';
import { api, downloadExport } from './App';
import { Check, Download } from './Icons';
import SpreadsheetImport from './SpreadsheetImport';

const PROVIDERS = ['BARBRI', 'BPP', 'Kaplan', 'Revise SQE', 'Epimeleia', 'ULaw', 'Other'];
const DEFAULT_STATUSES = ['Not started', 'Learning', 'Practised', 'Confident', 'Needs review'];

const PLANNER_COLORS = [
  { key: 'color_module',             label: 'Module sessions',      default: '#3b82f6' },
  { key: 'color_mcq',                label: 'MCQ sessions',         default: '#f43f5e' },
  { key: 'color_test',               label: 'Test sessions',        default: '#14b8a6' },
  { key: 'color_recall',             label: 'Active Recall Blurt',  default: '#8b5cf6' },
  { key: 'color_review_needs_work',  label: 'Review: needs work',  default: '#e05555' },
  { key: 'color_review_moderate',    label: 'Review: moderate',    default: '#d4903a' },
];

const DASH_TOGGLES = [
  { key: 'dash_show_countdown',   label: 'Exam countdowns',         desc: 'Shows days remaining to your FLK1 and FLK2 exams' },
  { key: 'dash_show_calibration', label: 'Calibration alerts',      desc: 'Flags topics where your confidence and MCQ accuracy disagree by 20% or more' },
  { key: 'dash_show_checkin',     label: 'Recall nudge',            desc: "Suggests topics you haven't tested yourself on recently" },
  { key: 'dash_show_priority',    label: 'Priority topics',         desc: 'Your weakest FLK1 and FLK2 topics, ranked by urgency' },
  { key: 'dash_show_touched',     label: 'How are you feeling?',    desc: 'Quick confidence sliders for your most urgent topics' },
  { key: 'dash_show_mcq',         label: 'MCQ milestone',           desc: 'Progress bar showing how many MCQs you have completed' },
  { key: 'dash_show_legend',      label: 'Priority tier legend',    desc: 'Explains what each priority level means' },
  { key: 'dash_show_principle',   label: 'Revision principles',     desc: 'Tips on effective revision techniques' },
];

function Toggle({ checked, onChange }) {
  return (
    <label className="settings-toggle">
      <input type="checkbox" checked={checked} onChange={onChange} />
      <span className="settings-toggle-track" />
    </label>
  );
}

export default function Settings({ onRestartTour }) {
  const [settings, setSettings] = useState({});
  const [user, setUser] = useState({});
  const [name, setName]       = useState('');
  const [flk1Date, setFlk1Date] = useState('');
  const [flk2Date, setFlk2Date] = useState('');
  const [provider, setProvider] = useState('');
  const [mcqTarget, setMcqTarget] = useState('40');
  const [currentPw, setCurrentPw] = useState('');
  const [newPw, setNewPw]         = useState('');
  const [pwError, setPwError]     = useState('');
  const [pwSaved, setPwSaved]     = useState(false);
  const [saved, setSaved] = useState({});
  const [showImport, setShowImport] = useState(false);
  // Status list (full ordered list stored in custom_statuses; empty = use defaults)
  const [allStatuses, setAllStatuses] = useState(DEFAULT_STATUSES);
  const [newStatus, setNewStatus] = useState('');
  // Spaced rep intervals
  const [srRedDays, setSrRedDays] = useState('7');
  const [srAmberDays, setSrAmberDays] = useState('14');

  useEffect(() => {
    Promise.all([api('/api/settings'), api('/api/me')]).then(([s, u]) => {
      setSettings(s);
      setUser(u);
      setName(u.name || '');
      setFlk1Date(s.flk1_exam_date || (u.exam_date_flk1 ? u.exam_date_flk1.slice(0, 10) : '') || '');
      setFlk2Date(s.flk2_exam_date || (u.exam_date_flk2 ? u.exam_date_flk2.slice(0, 10) : '') || '');
      setProvider(s.material_provider || '');
      setMcqTarget(s.mcq_daily_target || '40');
      try {
        const cs = JSON.parse(s.custom_statuses || '[]');
        setAllStatuses(cs.length > 0 ? cs : [...DEFAULT_STATUSES]);
      } catch { setAllStatuses([...DEFAULT_STATUSES]); }
      setSrRedDays(s.spaced_rep_red_days || '7');
      setSrAmberDays(s.spaced_rep_amber_days || '14');
    });
  }, []);

  const flash = (key) => {
    setSaved(prev => ({ ...prev, [key]: true }));
    setTimeout(() => setSaved(prev => ({ ...prev, [key]: false })), 2000);
  };

  const saveSetting = async (key, value) => {
    await api('/api/settings', { method: 'POST', body: JSON.stringify({ [key]: value }) });
    flash(key);
  };

  const saveName = async () => {
    if (!name.trim()) return;
    await api('/api/account', { method: 'PATCH', body: JSON.stringify({ name: name.trim() }) });
    flash('name');
  };

  const saveDates = async () => {
    const settingsUp = {};
    const accountUp = {};
    if (flk1Date) { settingsUp.flk1_exam_date = flk1Date; accountUp.exam_date_flk1 = flk1Date; }
    if (flk2Date) { settingsUp.flk2_exam_date = flk2Date; accountUp.exam_date_flk2 = flk2Date; }
    await Promise.all([
      api('/api/settings', { method: 'POST', body: JSON.stringify(settingsUp) }),
      api('/api/account',  { method: 'PATCH', body: JSON.stringify(accountUp) }),
    ]);
    flash('dates');
  };

  const saveMcqTarget = () => {
    const v = Math.max(1, Math.min(999, parseInt(mcqTarget) || 40)).toString();
    setMcqTarget(v);
    saveSetting('mcq_daily_target', v);
  };

  const saveAllStatuses = (list) => {
    setAllStatuses(list);
    saveSetting('custom_statuses', JSON.stringify(list));
  };
  const moveStatusUp = (idx) => {
    if (idx === 0) return;
    const u = [...allStatuses];
    [u[idx - 1], u[idx]] = [u[idx], u[idx - 1]];
    saveAllStatuses(u);
  };
  const moveStatusDown = (idx) => {
    if (idx === allStatuses.length - 1) return;
    const u = [...allStatuses];
    [u[idx], u[idx + 1]] = [u[idx + 1], u[idx]];
    saveAllStatuses(u);
  };
  const removeStatus = (idx) => {
    saveAllStatuses(allStatuses.filter((_, i) => i !== idx));
  };
  const addStatus = () => {
    const s = newStatus.trim();
    if (!s || allStatuses.includes(s)) return;
    saveAllStatuses([...allStatuses, s]);
    setNewStatus('');
  };

  const saveSpacedRep = () => {
    const red = Math.max(1, Math.min(90, parseInt(srRedDays) || 7)).toString();
    const amber = Math.max(1, Math.min(90, parseInt(srAmberDays) || 14)).toString();
    setSrRedDays(red); setSrAmberDays(amber);
    saveSetting('spaced_rep_red_days', red);
    saveSetting('spaced_rep_amber_days', amber);
  };

  const changePassword = async (e) => {
    e.preventDefault();
    setPwError('');
    if (newPw.length < 8) { setPwError('New password must be at least 8 characters'); return; }
    const res = await api('/api/change-password', {
      method: 'POST',
      body: JSON.stringify({ current_password: currentPw, new_password: newPw }),
    });
    if (res.ok) {
      setPwSaved(true); setCurrentPw(''); setNewPw('');
      setTimeout(() => setPwSaved(false), 3000);
    } else {
      setPwError(res.error || 'Failed to change password');
    }
  };

  if (showImport) {
    return <SpreadsheetImport onBack={() => setShowImport(false)} onDone={() => setShowImport(false)} />;
  }

  return (
    <div className="settings-page">
      <div className="settings-header">
        <div className="settings-title">Settings</div>
      </div>

      {/* ── Account ── */}
      <div className="settings-section">
        <div className="settings-section-title">Account</div>
        <div className="settings-row">
          <div className="settings-row-left">
            <span className="settings-row-label">Display name</span>
          </div>
          <div className="settings-row-right" style={{ gap: 8 }}>
            <input className="settings-text-input" value={name}
              onChange={e => setName(e.target.value)}
              onBlur={saveName}
              onKeyDown={e => e.key === 'Enter' && saveName()} />
            {saved['name'] && <span className="settings-inline-saved"><Check size={12} /> Saved</span>}
          </div>
        </div>
        <div className="settings-row">
          <div className="settings-row-left">
            <span className="settings-row-label">Email</span>
            <span className="settings-row-key">Read-only</span>
          </div>
          <div className="settings-row-right">
            <span className="settings-muted-val">{user.email || ''}</span>
          </div>
        </div>
        <div className="settings-pw-block">
          <span className="settings-row-label" style={{ marginBottom: 8, display: 'block' }}>Change password</span>
          <form className="settings-pw-form" onSubmit={changePassword}>
            <input className="settings-text-input" type="password" placeholder="Current password"
              value={currentPw} onChange={e => setCurrentPw(e.target.value)} autoComplete="current-password" />
            <input className="settings-text-input" type="password" placeholder="New password (8+ characters)"
              value={newPw} onChange={e => setNewPw(e.target.value)} autoComplete="new-password" />
            {pwError && <span className="settings-error">{pwError}</span>}
            {pwSaved && <span className="settings-inline-saved"><Check size={12} /> Password updated</span>}
            <button className="settings-save-btn" type="submit" style={{ alignSelf: 'flex-start' }}>
              Update password
            </button>
          </form>
        </div>
      </div>

      {/* ── Exam dates ── */}
      <div className="settings-section">
        <div className="settings-section-title">Exam dates</div>
        <div className="settings-row">
          <div className="settings-row-left">
            <span className="settings-row-label">FLK1</span>
          </div>
          <div className="settings-row-right">
            <input className="settings-date-input" type="date" value={flk1Date}
              onChange={e => setFlk1Date(e.target.value)} onBlur={saveDates} />
          </div>
        </div>
        <div className="settings-row">
          <div className="settings-row-left">
            <span className="settings-row-label">FLK2</span>
          </div>
          <div className="settings-row-right" style={{ gap: 8 }}>
            <input className="settings-date-input" type="date" value={flk2Date}
              onChange={e => setFlk2Date(e.target.value)} onBlur={saveDates} />
            {saved['dates'] && <span className="settings-inline-saved"><Check size={12} /> Saved</span>}
          </div>
        </div>
      </div>

      {/* ── Study materials ── */}
      <div className="settings-section">
        <div className="settings-section-title">Study materials</div>
        <div className="settings-row">
          <div className="settings-row-left">
            <span className="settings-row-label">Provider</span>
            <span className="settings-row-key">Used for MCQ import guidance</span>
          </div>
          <div className="settings-row-right" style={{ gap: 8 }}>
            <select className="settings-select" value={provider}
              onChange={e => { setProvider(e.target.value); saveSetting('material_provider', e.target.value); }}>
              <option value="">— Not set —</option>
              {PROVIDERS.map(p => <option key={p} value={p}>{p}</option>)}
            </select>
            {saved['material_provider'] && <span className="settings-inline-saved"><Check size={12} /> Saved</span>}
          </div>
        </div>
      </div>

      {/* ── MCQ target ── */}
      <div className="settings-section">
        <div className="settings-section-title">MCQ practice</div>
        <div className="settings-row">
          <div className="settings-row-left">
            <span className="settings-row-label">Daily MCQ target</span>
            <span className="settings-row-key">Questions you aim to do each day</span>
          </div>
          <div className="settings-row-right" style={{ gap: 8 }}>
            <input className="settings-number-input" type="number" min="1" max="999" value={mcqTarget}
              onChange={e => setMcqTarget(e.target.value)}
              onBlur={saveMcqTarget}
              onKeyDown={e => e.key === 'Enter' && e.target.blur()} />
            {saved['mcq_daily_target'] && <span className="settings-inline-saved"><Check size={12} /> Saved</span>}
          </div>
        </div>
      </div>

      {/* ── Topic statuses ── */}
      <div className="settings-section">
        <div className="settings-section-title">Topic statuses</div>
        <div style={{ padding: 'var(--sp-3) var(--sp-4) var(--sp-2)' }}>
          <p style={{ fontSize: 12, color: 'var(--muted)', margin: '0 0 12px', lineHeight: 1.5 }}>
            Customise the statuses shown in your topic tracker. Reorder, remove, or add your own. Changes apply immediately.
          </p>
          <div className="status-order-list">
            {allStatuses.map((s, idx) => (
              <div key={s + idx} className="status-order-row">
                <span className="status-order-name">{s}</span>
                <div className="status-order-controls">
                  <button className="status-order-btn" onClick={() => moveStatusUp(idx)} disabled={idx === 0} title="Move up">↑</button>
                  <button className="status-order-btn" onClick={() => moveStatusDown(idx)} disabled={idx === allStatuses.length - 1} title="Move down">↓</button>
                  <button className="status-order-btn status-order-remove" onClick={() => removeStatus(idx)} title="Remove">×</button>
                </div>
              </div>
            ))}
          </div>
          <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
            <input className="settings-text-input" value={newStatus} placeholder="Add a status…"
              onChange={e => setNewStatus(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && addStatus()}
              style={{ width: 160 }} />
            <button className="settings-action-btn" onClick={addStatus} disabled={!newStatus.trim()}>Add</button>
          </div>
        </div>
      </div>

      {/* ── Spaced repetition ── */}
      <div className="settings-section">
        <div className="settings-section-title">Spaced repetition</div>
        <div className="settings-row">
          <div className="settings-row-left">
            <span className="settings-row-label">Weak topics (under 50%)</span>
            <span className="settings-row-key">Days between revisits in planner & check-in nudge</span>
          </div>
          <div className="settings-row-right" style={{ gap: 8 }}>
            <input className="settings-number-input" type="number" min="1" max="90" value={srRedDays}
              onChange={e => setSrRedDays(e.target.value)}
              onBlur={saveSpacedRep}
              onKeyDown={e => e.key === 'Enter' && e.target.blur()} />
            <span style={{ fontSize: 12, color: 'var(--muted)' }}>days</span>
          </div>
        </div>
        <div className="settings-row">
          <div className="settings-row-left">
            <span className="settings-row-label">Moderate topics (50–69%)</span>
            <span className="settings-row-key">Days between revisits in planner & check-in nudge</span>
          </div>
          <div className="settings-row-right" style={{ gap: 8 }}>
            <input className="settings-number-input" type="number" min="1" max="90" value={srAmberDays}
              onChange={e => setSrAmberDays(e.target.value)}
              onBlur={saveSpacedRep}
              onKeyDown={e => e.key === 'Enter' && e.target.blur()} />
            <span style={{ fontSize: 12, color: 'var(--muted)' }}>days</span>
            {saved['spaced_rep_red_days'] && <span className="settings-inline-saved"><Check size={12} /> Saved</span>}
          </div>
        </div>
      </div>

      {/* ── Planner colours ── */}
      <div className="settings-section">
        <div className="settings-section-title">Planner colours</div>
        {PLANNER_COLORS.map(({ key, label, default: def }) => (
          <div key={key} className="settings-row">
            <div className="settings-row-left">
              <span className="settings-row-label">{label}</span>
            </div>
            <div className="settings-row-right" style={{ gap: 10 }}>
              <input
                type="color"
                value={settings[key] || def}
                className="settings-color-input"
                onChange={e => {
                  setSettings(prev => ({ ...prev, [key]: e.target.value }));
                  saveSetting(key, e.target.value);
                }}
              />
              <span style={{ fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--hint)' }}>
                {settings[key] || def}
              </span>
              {settings[key] && settings[key] !== def && (
                <button className="settings-action-btn" style={{ padding: '2px 8px', fontSize: 11 }}
                  onClick={() => { setSettings(prev => ({ ...prev, [key]: def })); saveSetting(key, def); }}>
                  Reset
                </button>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* ── Dashboard panels ── */}
      <div className="settings-section">
        <div className="settings-section-title">Dashboard panels</div>
        {DASH_TOGGLES.map(({ key, label, desc }) => (
          <div key={key} className="settings-row">
            <div className="settings-row-left">
              <span className="settings-row-label">{label}</span>
              {desc && <span className="settings-row-key">{desc}</span>}
            </div>
            <div className="settings-row-right">
              <Toggle
                checked={(settings[key] ?? '1') === '1'}
                onChange={e => {
                  const val = e.target.checked ? '1' : '0';
                  setSettings(prev => ({ ...prev, [key]: val }));
                  saveSetting(key, val);
                }}
              />
            </div>
          </div>
        ))}
      </div>

      {/* ── Data ── */}
      <div className="settings-section">
        <div className="settings-section-title">Data</div>
        <div className="settings-row">
          <div className="settings-row-left">
            <span className="settings-row-label">Export your data</span>
            <span className="settings-row-key">Downloads all topics and MCQ history as Excel</span>
          </div>
          <div className="settings-row-right">
            <button className="settings-action-btn" onClick={downloadExport}>
              <Download size={13} /> Export to Excel
            </button>
          </div>
        </div>
        <div className="settings-row">
          <div className="settings-row-left">
            <span className="settings-row-label">Import data</span>
            <span className="settings-row-key">Import confidence scores from a spreadsheet</span>
          </div>
          <div className="settings-row-right">
            <button className="settings-action-btn" onClick={() => setShowImport(true)}>
              Import spreadsheet
            </button>
          </div>
        </div>
      </div>

      {/* ── Testing ── */}
      <div className="settings-section">
        <div className="settings-section-title">Testing</div>
        <div className="settings-info-row">
          <span className="settings-info-label">New user preview</span>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4 }}>
            <button className="settings-newuser-btn"
              onClick={async () => {
                await api('/api/debug/reset-new-user', { method: 'POST' });
                window.location.reload();
              }}>
              Preview as new user →
            </button>
            <span style={{ fontSize: 11, color: 'var(--hint)' }}>Resets onboarding flags only. All data is kept.</span>
          </div>
        </div>
      </div>
    </div>
  );
}
